import React, { Fragment, useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { Trans } from '@lingui/react';
import { t } from '@lingui/macro';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import isEqual from 'lodash/isEqual';
import { useLocalisation } from '@3plearning/lingui-cdn';

// Styles
import '../form/form.scss';

// Components
import Button from '../button/button';
import SelectLabel from '../select-label/select-label';
import AddStudentsList from '../students-lists/add-students-list';
import EmptyDataMessage from '../empty-data-message/empty-data-message';
import AddStudentsListBody, { COPY_VALUE, MOVE_VALUE } from '../students-lists/add-students-list-body';
import GenericModal, { MODAL_TYPE } from '../generic-modal/generic-modal';

// Context
import ConsoleModalContext from '../../containers/console/context/console-modal-context';

// Actions
import consoleActions from '../../containers/console/console-actions';

// Helpers
import { ALL_GROUP_ID, DEFAULT_GROUP_ID, actionTypeIds, INVALID_CLASSNAME } from '../../helpers/enums';

// Styles
import './add-students.scss';

function setUpClassDropdown({ currentClasses, classDefaultLabel }) {
    // Set id = -1 for 'Select Class' option so that user can select all students again
    const listOfClassesDefault = [
        {
            value: INVALID_CLASSNAME,
            label: classDefaultLabel
        }
    ];
    const listOfClasses = currentClasses.map(oneClass => (
        {
            value: oneClass.id,
            label: oneClass.name
        }
    ));

    return [
        ...listOfClassesDefault,
        ...listOfClasses
    ];
}

// This will get back the available students that are not disabled
function getAvailableStudents({ currentStudents, currentStudentIds }) {
    const filteredCurrentStudents = currentStudents.map(student => student.userProfileId);
    const availableStudents = filteredCurrentStudents.filter(student => !currentStudentIds.includes(student));

    return availableStudents.map(studentId => parseInt(studentId, 10));
}

/*
    This function searches for the searched field in case that it's empty it would return false
    and display no students found
 */
function hasVisibleStudents(currentStudents, searchString) {
    const searchedStudent = currentStudents.filter((student) => {
        // To make searching students independent of case sensitiveness
        const searchStudent = (student.lastName.concat(' ', student.firstName)).toLowerCase();
        searchString = searchString.toLowerCase();
        if (searchStudent.includes(searchString)) {
            return student;
        }
        return false;
    });
    return searchedStudent;
}

function AddStudents(props) {
    const { i18n } = useLocalisation();

    // Props
    const {
        currentClassId,
        productId,
        // Action props
        getInitialAddStudent,
        getAddStudentClasses,
        updateCurrentClassId,
        assignClassStudent
    } = props;

    // State variables
    const [bottomErrorMessage, setBottomErrorMessage] = useState(null);
    // Required fields for add students
    const [showInitialMessage, setShowInitialMessage] = useState(true);
    const [currentClasses, setCurrentClasses] = useState([]);
    const [currentClassGroups, setCurrentClassGroups] = useState([]);
    const [studentClassData, setStudentClassData] = useState([]);
    const [availableGroups, setAvailableGroups] = useState([
        {
            id: '',
            name: i18n._(t`Select - Default Group Option`)
        }
    ]);
    // this variable will determine the students that should be disabled
    const [currentStudentIds, setCurrentStudentIds] = useState([]);
    const [currentStudents, setCurrentStudents] = useState([]);
    const [allGroupStudents, setAllGroupStudents] = useState([]);


    // Top Section - variables
    // To set value to 'Select Class' by default
    const [selectClassValue, setSelectClassValue] = useState(INVALID_CLASSNAME);
    const [selectGroupValue, setSelectGroupValue] = useState('');
    const [searchStudentName, setSearchStudentName] = useState('');

    // Selected students
    const [sourceGroupId, setSourceGroupId] = useState('');
    const [currentGroupName, setCurrentGroupName] = useState('');
    const [selectedStudents, setSelectedStudents] = useState({});
    const [selectAllValue, setSelectAllValue] = useState(false);
    const [isApplyButtonDisabled, setIsApplyButtonDisabled] = useState(false);
    const [classData, setClassData] = useState([]);

    // Context
    const { setShowConsoleModal, setConsoleModalContent } = useContext(ConsoleModalContext);

    // Effects
    // Effect to retrieve the available classes and initial groups which is at the bottom
    useEffect(() => {
        let isCancelled = false;
        const fetchInitialStudentDetail = async () => {
            const newStudentsData = await getInitialAddStudent({
                productId,
                currentClassId
            });
            const {
                classes,
                students
            } = newStudentsData;

            // Set Class data and Student data for global use
            setClassData(classes);
            setStudentClassData(students);

            if (!isCancelled && newStudentsData) {
                // Set the appropriate data for Student Management
                const updatedCurrentClass = classes.map((item) => {
                    if (item.id === 0) {
                        const name = i18n._(t`Select - unassign label`);
                        return {
                            id: item.id,
                            name
                        };
                    }
                    return {
                        id: item.id,
                        name: item.name
                    };
                });
                setCurrentClasses(updatedCurrentClass);
                // Set student data on initial load when Add Student button is clicked
                setCurrentStudents(students);
            }

            const currentClassData = await getAddStudentClasses({
                classId: currentClassId,
                productId
            });
            const {
                classGroups
            } = currentClassData;

            if (!isCancelled && currentClassData) {
                // Set default group ID to be 1 (defaultGroupId) in bottom select box of dialog
                setSourceGroupId(DEFAULT_GROUP_ID);

                // To make the classgroups object suitable for select box input
                const filteredClassGroups = classGroups.filter(item => item.groupId !== ALL_GROUP_ID)
                    .map(item => ({ id: item.groupId, name: [item.name] }));

                setCurrentClassGroups([
                    {
                        id: '',
                        name: i18n._(t`Select - Default Group Option`)
                    },
                    ...filteredClassGroups
                ]);
                const currentProfileIds = currentClassData.students.map(student => student.userProfileId);
                setCurrentStudentIds(currentProfileIds);
            } else {
                setBottomErrorMessage(i18n._(t`Error Message - Unable to proceed`));
            }
        };

        fetchInitialStudentDetail();

        return () => {
            isCancelled = true;
        };
    }, []);

    // Effect to retrieve the groups in the top section
    useEffect(() => {
        let isCancelled = false;
        const fetchStudentDetails = async () => {
            if (!isCancelled && studentClassData) {
                // To make the classgroups object suitable for select box input

                /* To reset all students data when user selects 'Select Class' option in drop down */
                if (parseInt(selectClassValue, 10) === -1) {
                    // Reset All Students data on click on 'Select Class' option in class drop down
                    setCurrentStudents(studentClassData);
                    return;
                }

                /*  Find the group list based on value selected in the group select and
                    map respective group data to groupListArray */
                const groupList = classData.filter(item =>
                    (item.id) === parseInt(selectClassValue, 10))[0].classroomGroups;
                const revisedClassGroups = groupList ?
                    groupList.map(item => ({ id: item.id, name: [item.name] })) : {};

                // Set the appropriate group data for in select box in top section
                setAvailableGroups([
                    {
                        id: 0,
                        name: i18n._(t`Select - Default Group Option`)
                    },
                    ...revisedClassGroups
                ]);

                // To display all groups or default group initially assigning 1
                setSelectGroupValue(0);

                /*  To find studentList in Class list from the global Class object
                    based on value selected in Select Class Box */
                const studentListArray = classData.filter(item => (item.id) === parseInt(selectClassValue, 10));

                const studentList = studentListArray.map(item => ({ id: item.studentsProfileId }))[0].id;

                const studentClassList = [];
                /*
                    Find student data from main list(studentClassData)matching the student list
                    created above to display students belonging to particular class
                    based on value selected in drop down
                */
                for (const index in studentList) {
                    const data = studentClassData.filter(item => item.userProfileId === studentList[index]);
                    if (data.length > 0) {
                        studentClassList.push(data[0]);
                    }
                }
                setCurrentStudents(studentClassList);
                // To set the All Groups students data for 'Select Group' option in Group drop down list
                setAllGroupStudents(studentClassList);
                /*
                    When there's only 1 group in the response and the id is set to be the default group,
                    set the name in the select box to be that name
                 */
                if (classData.length === 1 && classData[0].groupId === DEFAULT_GROUP_ID) {
                    setSelectGroupValue(1);
                }
            }

            // Reset the initial message, in order for the message to not be shown again
            if (showInitialMessage === true) {
                setShowInitialMessage(false);
            }
        };

        if (selectClassValue !== '') {
            setCurrentGroupName('');
            fetchStudentDetails();
        } else {
            // If the select value of the class is empty, reset the current students
            setCurrentStudents([]);
            setSelectGroupValue('');
            setCurrentGroupName('');
        }

        return () => {
            isCancelled = true;
        };
    }, [selectClassValue]);

    // Effect to retrieve the students after the group has changed
    useEffect(() => {
        let isCancelled = false;
        let studentGroupData;
        const fetchStudentDetails = async () => {
            if (!isCancelled) {
                if (parseInt(selectGroupValue, 10) === 0) {
                    // To display students from all groups
                    studentGroupData = allGroupStudents;
                } else {
                    // To display students from a particular group

                    /*  Find the group list based on value selected in the group select and
                        map respective group data to groupListArray */
                    const groupList = selectClassValue !== INVALID_CLASSNAME ? classData.filter(item =>
                        (item.id) === parseInt(selectClassValue, 10))[0].classroomGroups : null;
                    if (groupList === null) {
                        return;
                    }
                    /*  To find studentList in Class list from the global Class object
                        based on value selected in Select Class Box */
                    const groupListArray = groupList.filter(item => (item.id) === parseInt(selectGroupValue, 10));

                    const studentListArray = groupListArray.map(item => ({ id: item.studentsProfileId }));

                    const studentList = studentListArray === '' ? studentListArray : studentListArray[0].id;
                    const studentClassList = [];

                    /*
                        Find student data from main list(studentClassData)matching the student list
                        created above to display students belonging to particular class
                        based on value selected in Group drop down
                    */
                    for (const index in studentList) {
                        const data = studentClassData.filter(item => item.userProfileId === studentList[index]);
                        if (data.length > 0) {
                            studentClassList.push(data[0]);
                        }
                    }
                    studentGroupData = studentClassList;
                }
                // The response is valid
                if (studentGroupData) {
                    // Set the appropriate data for Student Management
                    setCurrentStudents(studentGroupData);
                    setSelectedStudents({});

                    // Set the correct name for the group name column
                    const groupNameItem = availableGroups.filter(item => item.id === parseInt(selectGroupValue, 10));
                    if (groupNameItem.length > 0) {
                        const firstValue = groupNameItem[0];
                        if (firstValue.id !== ALL_GROUP_ID) {
                            setCurrentGroupName(firstValue.name);
                        } else {
                            // As this case is for the "all groups", set the group name to be an empty string
                            setCurrentGroupName('');
                        }
                    }
                } else {
                    // Otherwise if it's invalid data, return back an empty list of current students
                    setCurrentStudents([]);
                    setSelectedStudents({});
                }
            }
        };

        if (selectClassValue !== '') {
            fetchStudentDetails();
        }

        return () => {
            isCancelled = true;
        };
    }, [selectGroupValue]);

    // Effect to determine the buttons disabled state based on the list of selected students
    useEffect(() => {
        const selectedStudentsLength = Object.keys(selectedStudents).length;

        setIsApplyButtonDisabled(selectedStudentsLength === 0 || sourceGroupId === '');
    }, [selectedStudents, sourceGroupId, selectAllValue]);

    const classDefaultLabel = i18n._(t`Class Dropdown - header`);
    const listOfClassesOptions = setUpClassDropdown({ currentClasses, classDefaultLabel });

    const selectAllCheckboxHandler = () => {
        const newSelectValue = !selectAllValue;
        const filteredCurrentStudents = currentStudents.filter(student => (student.fullName.toLowerCase())
            .includes(searchStudentName.toLowerCase()));
        const availableStudents = getAvailableStudents({
            currentStudents: filteredCurrentStudents,
            currentStudentIds
        });

        for (const index in availableStudents) {
            const studentId = availableStudents[index];
            const selectedValue = (
                typeof selectedStudents[studentId] === 'object'
            ) ?
                selectedStudents[studentId] : {
                    actionType: MOVE_VALUE
                };

            if (newSelectValue) {
                selectedStudents[studentId] = selectedValue;
            } else {
                delete selectedStudents[studentId];
            }
        }

        // Reverse the value of the select all
        setSelectAllValue(newSelectValue);
    };

    const individualCheckboxHandler = (studentId, actionType) => {
        const tempSelectedStudents = { ...selectedStudents };

        // If there wasn't a student without the id, then find it and remove it
        if (!tempSelectedStudents[studentId]) {
            tempSelectedStudents[studentId] = {
                actionType
            };
        } else if (actionType === undefined) {
            // If it was deselected, set the and the actionType is udnefined, remove the studentId
            delete tempSelectedStudents[studentId];
        } else {
            tempSelectedStudents[studentId] = {
                actionType
            };
        }

        setSelectedStudents(tempSelectedStudents);

        // test if the checkbox conflicts with the select all
        const filteredCurrentStudents = currentStudents.filter(student => (student.fullName.toLowerCase())
            .includes(searchStudentName.toLocaleLowerCase()));

        const filteredStudentIds = Object.keys(tempSelectedStudents).map(student => parseInt(student, 10));
        if (!isEqual(filteredStudentIds, filteredCurrentStudents)) {
            setSelectAllValue(false);
        }
    };
    const moveCopyHandler = (studentId, actionType) => {
        const tempSelectedStudents = { ...selectedStudents };

        tempSelectedStudents[studentId] = {
            actionType
        };

        setSelectedStudents(tempSelectedStudents);
    };
    const closeConfirmationModal = () => {
        setShowConsoleModal(false);
    };
    const selectHandlerClasses = e => setSelectClassValue(e.target.value);
    const selectHandlerGroups = e => setSelectGroupValue(e.target.value);
    const inputHandlerName = e => setSearchStudentName(e.target.value);
    const updateCurrentGroup = e => setSourceGroupId(e.target.value);

    const params = {
        currentClassId,
        selectClassValue,
        sourceGroupId
    };

    const setUpSaveInformation = () => {
        const tempStudents = { ...selectedStudents };
        const tempUpdateStudents = {};
        let studentIndex = 0;

        // To create an array of student data to move/copy action
        for (const studentId in tempStudents) {
            const parsedStudentId = parseInt(studentId, 10);
            // To find selected student(checkbox clicked) from student list
            const selectedStudent = studentClassData.filter(item => (item.userProfileId === parsedStudentId));
            // To get all classes of the student selected
            const classArrayList = selectedStudent[0].assignedClasses.map(item => item.classId);
            if (tempStudents[studentId].actionType === MOVE_VALUE ||
                tempStudents[studentId].actionType === COPY_VALUE) {
                /* If student is selected in "Select Class" option,
                get all the classes of that student for move/copy functionality */
                if (classArrayList.length > 1 && selectClassValue === INVALID_CLASSNAME) {
                    for (const classIndex in classArrayList) {
                        tempUpdateStudents[studentIndex] = {
                            destinationClassId: parseInt(params.currentClassId, 10),
                            destinationGroupId: parseInt(params.sourceGroupId, 10),
                            sourceClassId: parseInt(classArrayList[classIndex], 10),
                            studentProfileId: parsedStudentId,
                            actionType: actionTypeIds[tempStudents[studentId].actionType]
                        };
                        studentIndex += 1;
                    }
                } else if (classArrayList.length === 0) {
                    // Set source id = 0 for students belonging to 'Unassigned Class'
                    tempUpdateStudents[parsedStudentId] = {
                        destinationClassId: parseInt(params.currentClassId, 10),
                        destinationGroupId: parseInt(params.sourceGroupId, 10),
                        sourceClassId: 0,
                        studentProfileId: parsedStudentId,
                        actionType: actionTypeIds[tempStudents[studentId].actionType]
                    };
                } else {
                    /* Set source class id to value from where student should be move/copy
                    when class is selected from drop down of Class drop down list. If the student
                    is moved from all students list, assign the value of classId from the
                    classArraylist. If particular class is selected from dropdown and then student
                    is moved, then assign the value of classId from the dropdown class selected */
                    const classId = selectClassValue === INVALID_CLASSNAME ? classArrayList[0] : selectClassValue;
                    tempUpdateStudents[parsedStudentId] = {
                        destinationClassId: parseInt(params.currentClassId, 10),
                        destinationGroupId: parseInt(params.sourceGroupId, 10),
                        sourceClassId: parseInt(classId, 10),
                        studentProfileId: parsedStudentId,
                        actionType: actionTypeIds[tempStudents[studentId].actionType]
                    };
                }
            }
        }

        return {
            tempUpdateStudents
        };
    };

    const saveHandler = async (e) => {
        // Prevent redirect from the default behaviour of a form
        e.preventDefault();

        // Set up the list of copy/move students
        const {
            tempUpdateStudents
        } = setUpSaveInformation();

        // Map studentId with tempUpdateStudents array to remove studentId index
        const listOfStudentsToUpdate = Object.keys(tempUpdateStudents);
        const assignStudents = listOfStudentsToUpdate.map(student => tempUpdateStudents[student]);

        const assignStudentsBody = {
            assignStudents
        };

        const addStudentsResponse = await assignClassStudent(assignStudentsBody);

        // Success/Error handling
        if (addStudentsResponse !== false) {
            setConsoleModalContent((
                <GenericModal
                    title={<Trans id={'Add Modal - Success Title'} />}
                    closeHandler={closeConfirmationModal}
                    parentClasses="modal--small"
                    type={MODAL_TYPE.floating}>
                    <div className="form__message">
                        <Trans id="Add Modal - Success Message" /> <span className="add-students__tick" />
                    </div>
                    <div className="form__row form__row--buttons">
                        <Button
                            callback={closeConfirmationModal}
                            visibleText={i18n._(t`Button - Ok`)} />
                    </div>
                </GenericModal>
            ));
            updateCurrentClassId({ productId, classId: currentClassId });
        } else {
            // If there was an error, show a message that something happened
            setBottomErrorMessage(i18n._(t`Update students - Error message`));
        }
    };


    /*
        This effect will reset the selected students' checkbox and the select all,
        to start clean whenever the class changes or group changes
     */
    useEffect(() => {
        setSelectedStudents({});
        setSelectAllValue(false);
    }, [selectClassValue, selectGroupValue]);

    useEffect(() => {
        // remove the selected student if it's part of the name when the teacher has selected them
        const searchName = searchStudentName.toLowerCase();
        currentStudents.map((student) => {
            const studentFullName = student.fullName.toLowerCase();
            if (!studentFullName.includes(searchName)) {
                if (selectedStudents[student.userProfileId]) {
                    delete selectedStudents[student.userProfileId];
                }
            }
            return student;
        });

        const filteredCurrentStudents = currentStudents.filter((student) => {
            const studentFullName = student.fullName.toLowerCase();
            return studentFullName.includes(searchName);
        });

        const filteredStudentIds = Object.keys(selectedStudents)
            .map((studentId) => {
                if (selectedStudents[studentId]) {
                    return parseInt(studentId, 10);
                }

                return false;
            })
            .filter(item => item !== false);

        if (!isEqual(filteredStudentIds, filteredCurrentStudents)) {
            setSelectAllValue(false);
        }
    }, [searchStudentName]);

    const isUnassignedStudents = (
        selectClassValue === '' || selectClassValue === `${ALL_GROUP_ID}` || availableGroups.length === 0 ||
        selectClassValue === INVALID_CLASSNAME
    );
    const listOfRows = currentStudents && currentStudents.length > 0 &&
        hasVisibleStudents(currentStudents, searchStudentName) ?
        currentStudents.map(
            (row, index) => {
                const isDisabled = currentStudentIds.includes(row.userProfileId);
                const searchStudent = searchStudentName.toLocaleLowerCase();
                // to show students selected in search text box(not case sensitive)
                const isVisible = (row.lastName.concat(' ', row.firstName).toLowerCase()).includes(searchStudent);
                const isSelected = typeof selectedStudents[row.userProfileId] === 'object';
                const revisedFullName = row.lastName.concat(', ', row.firstName);
                let studentGroupName = [];
                let studentClassName = [];
                // Make selectClassValue blank if user clicks on Select Class option to populate all students
                const classValueSelected = selectClassValue === INVALID_CLASSNAME ? '' : selectClassValue;
                const classSelected = parseInt(classValueSelected, 10);
                if (row.assignedClasses) {
                    if (row.assignedClasses.length > 0) {
                        /* To show only one class name which is selected in Class Select box
                        or to show all the class names to which students belong when clicked on Select Class option */
                        row.assignedClasses.map((item, classindex) => {
                            if (classSelected === item.classId || isNaN(classSelected)) {
                                studentClassName[classindex] = item.className;
                                studentGroupName[classindex] = item.groupName;
                            }
                            return {
                                studentClassName,
                                studentGroupName
                            };
                        });
                    } else {
                        studentClassName = ['-'];
                        studentGroupName = ['-'];
                    }
                }
                return (
                    <AddStudentsListBody
                        key={`move-students-${row.userProfileId}-${row.fullName}`}
                        userId={row.userProfileId}
                        index={index}
                        isSelected={isSelected}
                        isVisible={isVisible}
                        fullName={revisedFullName}
                        classValue={studentClassName}
                        groupName={currentGroupName === '' ? studentGroupName : currentGroupName}
                        checkboxHandler={individualCheckboxHandler}
                        moveCopyHandler={moveCopyHandler}
                        isDisabled={isDisabled}
                    />
                );
            }
        ) :
        [
            (
                <EmptyDataMessage
                    key="emptyRowAdd"
                    showInitialAddMessage={showInitialMessage}
                    initialMessage={(
                        <Trans id="Add Modal - initial message" />
                    )}
                    colSpan={5}
                />
            )
        ];

    // External Components
    const tableComponent = (
        <table className="table add-students__search">
            <tbody>
                <tr>
                    <td className="table__cell table__cell--naked">
                        <SelectLabel
                            id="add-search-classes"
                            srLabel={i18n._(t`Add Modal - Search - Classes`)}
                            listOfOptions={listOfClassesOptions}
                            propertyName="value"
                            visibleProperty="label"
                            value={selectClassValue}
                            selectHandler={selectHandlerClasses} />
                    </td>
                    <td className="table__cell table__cell--naked">
                        <SelectLabel
                            id="add-search-groups"
                            srLabel={i18n._(t`Add Modal - Search - Groups`)}
                            listOfOptions={availableGroups}
                            propertyName="id"
                            visibleProperty="name"
                            value={selectGroupValue}
                            isSelectDisabled={isUnassignedStudents}
                            selectHandler={selectHandlerGroups} />
                    </td>
                    <td className="table__cell table__cell--naked">
                        <label htmlFor="add-search-name" className="sr-only">
                            <Trans id={'Add Modal - Search - Enter name'} />
                        </label>
                        <input
                            type="text"
                            id="add-search-name"
                            value={searchStudentName}
                            placeholder={i18n._(t`Add Modal - Search - Enter name`)}
                            onChange={inputHandlerName}
                            className="form__input"
                            autoComplete="off"
                        />
                    </td>
                </tr>
            </tbody>
        </table>
    );

    return (
        <Fragment>
            {tableComponent}
            <div className="modal__variable-height">
                <AddStudentsList
                    selectAllValue={selectAllValue}
                    selectAllCheckboxHandler={selectAllCheckboxHandler}
                    numberOfStudents={currentStudents.length}
                    listOfRows={listOfRows}
                />
            </div>
            <Trans id={`Add or Move Modal - note message - product ${productId}`}>
                <p className="form__message">
                    <span className="form__note">Title</span>
                    Message
                </p>
            </Trans>
            <div className="form__row form__row--buttons">
                <p>
                    <label htmlFor="add-to-group" className="form__message form__message--label">
                        <Trans id={'Add Modal - add to this group'} />
                    </label>
                    <SelectLabel
                        id="add-to-group"
                        listOfOptions={currentClassGroups}
                        propertyName="id"
                        visibleProperty="name"
                        selectHandler={updateCurrentGroup}
                        value={sourceGroupId}
                        isRequired
                        importedClasses="select--content" />
                </p>
                <Button
                    callback={saveHandler}
                    isBtnDisabled={isApplyButtonDisabled}
                    visibleText={i18n._(t`Add Modal - Apply`)} />
            </div>
            {
                bottomErrorMessage ? (
                    <div className="form__row">
                        <p className="form__error-message form__error-message--bottom">
                            {bottomErrorMessage}
                        </p>
                    </div>
                ) : ''
            }
        </Fragment>
    );
}

AddStudents.propTypes = {
    currentClassId: PropTypes.number.isRequired,
    // State props
    productId: PropTypes.number,
    // Action props
    getInitialAddStudent: PropTypes.func,
    getAddStudentClasses: PropTypes.func,
    updateCurrentClassId: PropTypes.func,
    assignClassStudent: PropTypes.func
};

/*
 * mapStateToProps - set component properties from current state provided by the Reducers.
 * @param state {object} - the revised state from MessageModalCombinedReducers.
 */
function mapStateToProps(state) {
    const {
        productId
    } = state.consoleReducer;

    return {
        productId
    };
}

/*
 * mapDispatchToProps - ensure that the outputs of the Action Creators are passed to the Reducers
 * @param dispatch
 */
function mapDispatchToProps(dispatch) {
    return bindActionCreators({
        getInitialAddStudent: consoleActions.getInitialAddStudent,
        getInitialClassAddStudent: consoleActions.getInitialClassAddStudent,
        getAddStudentClasses: consoleActions.getAddStudentClasses,
        getAddStudentDetails: consoleActions.getAddStudentDetails,
        updateCurrentClassId: consoleActions.updateCurrentClassId,
        assignClassStudent: consoleActions.assignClassStudent
    }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(AddStudents);



// WEBPACK FOOTER //
// ./components/add-students/add-students.js